import { z } from 'zod';

import type { PutApiV1SerievideoIdMutationRequest } from '@/shared/api/generated';

/**
 * Schema do formulário de Vídeo (criar/editar). Validado por Zod (React Hook
 * Form + resolver).
 *
 * NÃO derivado de `serieVideoViewModelSchema` (gerado pelo Kubb): aquele schema
 * é exportado com o cast `as unknown as ToZod<SerieVideoViewModel>`, o que torna
 * a shape do `ZodObject` uma ficção de tipo — qualquer `.pick()`/`.extend()`
 * sobre ele degrada o `z.infer` para `never` (mesmo problema documentado em
 * `location/types/countryForm.ts`). Por isso o schema é definido aqui
 * diretamente. Note também que o contrato de **escrita**
 * (`PostApiV1SerievideoSeriesSerieidMutationRequest`/`PutApiV1SerievideoIdMutationRequest`,
 * multipart/form-data) usa PascalCase e não é o mesmo shape do
 * `SerieVideoViewModel` (leitura, camelCase) — o mapeamento entre os dois vive
 * em `useSaveSerieVideo`.
 */
export const serieVideoFormSchema = z.object({
  title: z.string().trim().min(1),
  date: z.string().trim().min(1),
  description: z.string().trim().min(1),
  channel: z.string().trim().min(1),
  thumbnailFile: z.instanceof(File).nullable(),
  removeThumbnail: z.boolean(),
  active: z.boolean(),
});

/**
 * Valores do formulário de Vídeo (create/edit).
 *
 * O tipo intersecta com uma asserção nula (no-op) que só permanece `unknown`
 * enquanto os campos usados por `useSaveSerieVideo` continuarem existindo em
 * `PutApiV1SerievideoIdMutationRequest` — se um for renomeado/removido no
 * contrato gerado pelo Kubb, a asserção vira `never` e o `yarn type-check`
 * quebra aqui.
 */
export type SerieVideoFormValues = z.infer<typeof serieVideoFormSchema> &
  (
    | 'Date'
    | 'Title'
    | 'Resumee'
    | 'Embed'
    | 'Active'
    | 'Thumbnail'
    | 'RemoveThumbnail' extends keyof PutApiV1SerievideoIdMutationRequest
    ? unknown
    : never);
